import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import { DataGrid, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import dayjs from 'dayjs';
import {
	ExtractionLogCell,
	ExtractionLogRow,
	getMessageRerunStatus,
	listPendingMessages,
	RerunResult,
	startMessageRerun,
	StatusFilter,
} from '../../services/messageQueue.service';
import { ResolveExtractionLogDialog } from '../../components/Dialog/ResolveExtractionLogDialog';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PAGE_SIZE_STORAGE_KEY = 'workerMessages.pageSize';
const RERUN_POLL_MS = 5000;
// Give up on progress checks after about a minute of the API not answering.
const MAX_FAILED_RERUN_POLLS = 12;

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

const rerunSummary = (finished: Array<[number, RerunResult | undefined]>): string => {
	const completed = finished.filter(([, result]) => result?.state === 'completed').length;
	const review = finished.filter(([, result]) => result?.state === 'needs_review').length;
	const parserOnly = finished.filter(([, result]) => result?.state === 'needs_review' && result.parserOnly).length;
	const failed = finished.filter(([, result]) => result?.state === 'failed');
	// No result means the server no longer knows about the rerun, usually
	// because the API restarted while it was queued or running.
	const interrupted = finished.filter(([, result]) => !result).length;
	const parts = [
		completed > 0 ? `${completed} moved to Completed (Work Done created)` : null,
		review > 0
			? `${review} still need review${
					parserOnly > 0 ? ` (AI did not answer for ${parserOnly}; the parser's reading was kept)` : ''
			  }`
			: null,
		failed.length > 0 ? `${failed.length} could not be rerun` : null,
		interrupted > 0 ? `${interrupted} interrupted (the server restarted) — please rerun` : null,
	].filter(Boolean);
	const firstError = failed.find(([, result]) => result?.state === 'failed')?.[1] as
		| { state: 'failed'; error: string }
		| undefined;
	return `Rerun finished for ${plural(finished.length, 'message')}: ${parts.join(', ')}.${
		firstError ? ` (${firstError.error})` : ''
	}`;
};

// The chosen page size is a per-browser preference; storage can be blocked
// (private mode, cleared site data), so fall back to the default silently.
const readStoredPageSize = (): number => {
	try {
		const stored = Number(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
		return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
	} catch {
		return DEFAULT_PAGE_SIZE;
	}
};

const storePageSize = (size: number) => {
	try {
		window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
	} catch {
		// Not remembered this time; the selection still applies on this page.
	}
};

const unique = (values: Array<string | null | undefined>): string[] =>
	Array.from(new Set(values.map((value) => value?.trim()).filter((value): value is string => !!value)));

const getWorkers = (row: ExtractionLogRow): string => {
	const names: string[] = [];
	for (const cell of row.cells) {
		if (cell.participants?.length) {
			names.push(...cell.participants.map((participant) => participant.name).filter((name): name is string => !!name));
		} else {
			if (cell.labels.senderName) names.push(cell.labels.senderName);
			names.push(...(cell.coWorkers ?? []).map((worker) => worker.name));
		}
	}
	const result = unique(names);
	return result.length > 0 ? result.join(', ') : row.senderName ?? row.fromPhone ?? 'Unknown';
};

const getProjects = (row: ExtractionLogRow): string => {
	const projects = unique(row.cells.map((cell) => cell.labels.projectName));
	return projects.length > 0 ? projects.join(', ') : 'Needs selection';
};

const cellWorkLabel = (cell: ExtractionLogCell): string =>
	[cell.labels.typeName, cell.labels.subTypeName, cell.labels.subTypeValue].filter(Boolean).join(' · ');

const getWorkSummary = (row: ExtractionLogRow): string => {
	const work = unique(row.cells.map(cellWorkLabel));
	if (work.length === 0) return row.messageType === 'work-done' ? 'Needs selection' : 'Review message';
	const locationCount = row.cells.length;
	return `${work.join(', ')} · ${locationCount} location${locationCount === 1 ? '' : 's'}`;
};

const cellLocationLabel = (cell: ExtractionLogCell): string => {
	const hasStructuredLocation =
		cell.labels.blockNumber != null && cell.resolved.level.value != null && cell.resolved.unit.value != null;
	if (!hasStructuredLocation) return cell.reportedLocationText?.trim() || 'Needs review';
	return [
		cell.labels.precinctName,
		cell.labels.blockNumber ? `Blk ${cell.labels.blockNumber}` : null,
		cell.resolved.level.value != null ? `L${cell.resolved.level.value}` : null,
		cell.resolved.unit.value != null ? `U${cell.resolved.unit.value}` : null,
	]
		.filter(Boolean)
		.join(' · ');
};

const getLocationSummary = (row: ExtractionLogRow): string => {
	const locations = unique(row.cells.map(cellLocationLabel));
	if (locations.length === 0) return 'Needs review';
	if (locations.length <= 2) return locations.join(' | ');
	return `${locations.slice(0, 2).join(' | ')} +${locations.length - 2} more`;
};

const hasEvidence = (row: ExtractionLogRow): boolean => (row.evidence?.length ?? 0) > 0;

export function MessageQueue() {
	const [rows, setRows] = useState<ExtractionLogRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(readStoredPageSize);
	const [total, setTotal] = useState(0);
	const [selected, setSelected] = useState<ExtractionLogRow | null>(null);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
	const [rerunningIds, setRerunningIds] = useState<number[]>([]);
	const [startingRerun, setStartingRerun] = useState(false);
	const failedRerunPolls = useRef(0);

	const loadRows = useCallback(async (pageNumber: number, limit: number, status: StatusFilter) => {
		try {
			setLoading(true);
			const result = await listPendingMessages(pageNumber, limit, status, 'all');
			if (result.success) {
				setRows(result.data?.rows || []);
				setTotal(result.data?.totalItems || 0);
			} else {
				toast.error(result.message || 'Failed to load worker messages.');
			}
		} catch (err: any) {
			toast.error(err?.response?.data?.message || err?.message || 'Failed to load worker messages.');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadRows(page, pageSize, statusFilter);
	}, [page, pageSize, statusFilter, loadRows]);

	useEffect(() => {
		setPage(0);
	}, [statusFilter]);

	const rerunInProgress = rerunningIds.length > 0;

	// Only one rerun at a time. A rerun started before a page refresh (or in
	// another tab) is still running on the server, so pick it up on load.
	useEffect(() => {
		getMessageRerunStatus([])
			.then((status) => {
				const running = status?.data?.running ?? [];
				if (running.length > 0) setRerunningIds(running);
			})
			.catch(() => undefined);
	}, []);

	// Selection only applies to the rows currently on screen.
	useEffect(() => {
		setSelectionModel([]);
	}, [statusFilter, page, pageSize]);

	const handleRerun = async () => {
		const ids = selectionModel.map(Number);
		if (ids.length === 0 || rerunInProgress) return;
		try {
			setStartingRerun(true);
			const result = await startMessageRerun(ids);
			if (!result.success || !result.data) {
				toast.error(result.message || 'Rerun could not be started.');
				return;
			}
			const { accepted, skipped } = result.data;
			if (accepted.length > 0) {
				toast.info(`Rerunning ${plural(accepted.length, 'message')}. This can take up to a minute per message.`);
				setRerunningIds((current) => Array.from(new Set([...current, ...accepted])));
			}
			if (skipped.length > 0) {
				toast.warning(
					`Skipped ${plural(skipped.length, 'message')}: ${skipped
						.map((item) => `#${item.id} ${item.reason}`)
						.join('; ')}`
				);
			}
			setSelectionModel([]);
		} catch (err: any) {
			toast.error(err?.response?.data?.message || err?.message || 'Rerun could not be started.');
		} finally {
			setStartingRerun(false);
		}
	};

	// While reruns are in progress, poll the API; when some finish, report the
	// outcome and refresh the list so completed rows leave Needs review.
	useEffect(() => {
		if (rerunningIds.length === 0) return;
		const timer = window.setInterval(async () => {
			try {
				const status = await getMessageRerunStatus(rerunningIds);
				failedRerunPolls.current = 0;
				const stillRunning = new Set(status.data.running);
				const finished = rerunningIds.filter((id) => !stillRunning.has(id));
				if (finished.length === 0) return;
				const outcomes = finished.map((id) => [id, status.data.results[id]] as [number, RerunResult | undefined]);
				const anyFailed = outcomes.some(([, result]) => !result || result.state === 'failed');
				(anyFailed ? toast.warning : toast.success)(rerunSummary(outcomes));
				setRerunningIds((current) => current.filter((id) => !finished.includes(id)));
				loadRows(page, pageSize, statusFilter);
			} catch (err: any) {
				// An expired session never recovers by waiting; a network blip
				// usually does, but not forever.
				failedRerunPolls.current += 1;
				const sessionExpired = err?.response?.status === 401;
				if (sessionExpired || failedRerunPolls.current >= MAX_FAILED_RERUN_POLLS) {
					failedRerunPolls.current = 0;
					setRerunningIds([]);
					toast.error(
						sessionExpired
							? 'Your session expired, so rerun progress can no longer be checked. Log in again, then rerun any rows still in Needs review.'
							: 'Lost contact with the server while checking rerun progress. Refresh the page, then rerun any rows still in Needs review.'
					);
				}
			}
		}, RERUN_POLL_MS);
		return () => window.clearInterval(timer);
	}, [rerunningIds, page, pageSize, statusFilter, loadRows]);

	const columns: GridColDef[] = [
		{
			field: 'message',
			headerName: 'Message',
			minWidth: 260,
			flex: 1.35,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as ExtractionLogRow;
				const date = row.messageTimestamp || row.createdAt;
				return (
					<Box sx={{ minWidth: 0, width: '100%' }}>
						<Typography variant='body2' sx={{ fontWeight: 650, color: '#1e293b' }} noWrap>
							{row.rawMessage || 'No message text'}
						</Typography>
						<Typography variant='caption' color='text.secondary'>
							{date ? dayjs(date).format('DD MMM YYYY, HH:mm') : 'Unknown date'}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: 'assignment',
			headerName: 'Assignment',
			minWidth: 150,
			flex: 0.75,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as ExtractionLogRow;
				return (
					<Box sx={{ minWidth: 0, width: '100%' }}>
						<Typography variant='body2' sx={{ fontWeight: 650 }} noWrap>
							{getWorkers(row)}
						</Typography>
						<Typography variant='caption' color='text.secondary' noWrap>
							{getProjects(row)}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: 'workSummary',
			headerName: 'Work',
			minWidth: 190,
			flex: 1,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as ExtractionLogRow;
				return (
					<Tooltip title={unique(row.cells.map(cellWorkLabel)).join(' | ')} arrow>
						<Typography variant='body2' sx={{ color: '#334155' }} noWrap>
							{getWorkSummary(row)}
						</Typography>
					</Tooltip>
				);
			},
		},
		{
			field: 'locationSummary',
			headerName: 'Location',
			minWidth: 165,
			flex: 0.9,
			sortable: false,
			valueGetter: (params) => getLocationSummary(params.row as ExtractionLogRow),
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip title={unique(params.row.cells.map(cellLocationLabel)).join(' | ')} arrow>
					<Typography variant='body2' color='text.secondary' noWrap>
						{params.value as string}
					</Typography>
				</Tooltip>
			),
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 168,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as ExtractionLogRow;
				if (rerunningIds.includes(row.id)) {
					return (
						<Chip
							size='small'
							label='Rerunning…'
							color='info'
							variant='outlined'
							icon={<CircularProgress size={12} color='inherit' />}
							sx={{ fontWeight: 650, bgcolor: '#f0f7ff' }}
						/>
					);
				}
				return (
					<Stack direction='row' alignItems='center' spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
						<Chip
							size='small'
							label={row.status === 'pending' ? 'Needs review' : 'Completed'}
							color={row.status === 'pending' ? 'warning' : 'success'}
							variant='outlined'
							sx={{ flexShrink: 0, fontWeight: 650, bgcolor: row.status === 'pending' ? '#fffaf0' : '#f2fbf5' }}
						/>
						{hasEvidence(row) && (
							<Tooltip title='Photo attached'>
								<ImageOutlinedIcon
									color='primary'
									fontSize='small'
									aria-label='Evidence attached'
									sx={{ flexShrink: 0 }}
								/>
							</Tooltip>
						)}
					</Stack>
				);
			},
		},
		{
			field: 'actions',
			headerName: '',
			width: 96,
			sortable: false,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => (
				<Button
					size='small'
					variant={params.row.status === 'pending' ? 'contained' : 'outlined'}
					disableElevation
					onClick={(event) => {
						event.stopPropagation();
						setSelected(params.row as ExtractionLogRow);
					}}
					sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
					{params.row.status === 'pending' ? 'Review' : 'View'}
				</Button>
			),
		},
	];

	return (
		<Box sx={{ flex: 1, height: '100%', maxWidth: 1480, mx: 'auto', width: '100%' }}>
			<Helmet>
				<title>Worker Messages</title>
			</Helmet>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				justifyContent='space-between'
				alignItems={{ sm: 'center' }}
				spacing={1.5}
				sx={{ mb: 2 }}>
				<Box>
					<Typography variant='h5' component='h2' sx={{ fontWeight: 750, letterSpacing: '-0.02em' }}>
						Worker Messages
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Review work reported through WhatsApp.
					</Typography>
				</Box>
				<Stack direction='row' alignItems='center' spacing={1.5}>
					{statusFilter === 'pending' && (
						<Tooltip
							title={
								rerunInProgress
									? `A rerun is in progress (${plural(rerunningIds.length, 'message')}). Wait for it to finish.`
									: 'Run the selected messages through the parser and AI again'
							}>
							<span>
								<Button
									variant='outlined'
									disableElevation
									startIcon={
										startingRerun || rerunInProgress ? <CircularProgress size={16} color='inherit' /> : <ReplayIcon />
									}
									disabled={selectionModel.length === 0 || startingRerun || rerunInProgress}
									onClick={handleRerun}
									sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
									{rerunInProgress
										? 'Rerun in progress…'
										: `Rerun${selectionModel.length > 0 ? ` (${selectionModel.length})` : ''}`}
								</Button>
							</span>
						</Tooltip>
					)}
					<ToggleButtonGroup
						value={statusFilter}
						exclusive
						size='small'
						onChange={(_, value) => value && setStatusFilter(value as StatusFilter)}
						sx={{
							p: 0.5,
							bgcolor: '#eef2f7',
							borderRadius: 2.5,
							'& .MuiToggleButton-root': {
								px: 2,
								py: 0.65,
								border: 0,
								borderRadius: '8px !important',
								textTransform: 'none',
								fontWeight: 700,
								color: '#64748b',
							},
							'& .Mui-selected': {
								bgcolor: '#fff !important',
								color: '#1d4ed8',
								boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)',
							},
						}}>
						<ToggleButton value='pending'>Needs review</ToggleButton>
						<ToggleButton value='done'>Completed</ToggleButton>
					</ToggleButtonGroup>
				</Stack>
			</Stack>
			<Paper
				variant='outlined'
				sx={{
					height: 620,
					width: '100%',
					overflow: 'hidden',
					borderRadius: 3,
					borderColor: '#e2e8f0',
					boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
				}}>
				<DataGrid
					rows={rows}
					columns={columns}
					loading={loading}
					rowCount={total}
					page={page}
					pageSize={pageSize}
					rowsPerPageOptions={PAGE_SIZE_OPTIONS}
					onPageSizeChange={(size) => {
						setPageSize(size);
						storePageSize(size);
						setPage(0);
					}}
					pagination
					paginationMode='server'
					checkboxSelection={statusFilter === 'pending'}
					selectionModel={selectionModel}
					onSelectionModelChange={setSelectionModel}
					isRowSelectable={() => !rerunInProgress}
					onPageChange={setPage}
					disableSelectionOnClick
					onCellClick={(params) => {
						// Ticking a row's checkbox selects it for Rerun; it must not open the review.
						if (params.field === GRID_CHECKBOX_SELECTION_COL_DEF.field) return;
						setSelected(params.row as ExtractionLogRow);
					}}
					rowHeight={72}
					headerHeight={48}
					sx={{
						cursor: 'pointer',
						border: 0,
						color: '#334155',
						'& .MuiDataGrid-columnHeaders': {
							bgcolor: '#f8fafc',
							borderBottom: '1px solid #e2e8f0',
						},
						'& .MuiDataGrid-columnHeaderTitle': { fontWeight: 750, color: '#475569' },
						'& .MuiDataGrid-columnSeparator': { display: 'none' },
						'& .MuiDataGrid-cell': {
							px: 2,
							alignItems: 'center',
							borderBottom: '1px solid #edf1f5',
							outline: 'none !important',
						},
						'& .MuiDataGrid-row:hover': { bgcolor: '#f8fbff' },
						'& .MuiDataGrid-footerContainer': { borderTop: '1px solid #e2e8f0', minHeight: 54 },
						'& .MuiDataGrid-virtualScroller': { bgcolor: '#fff' },
					}}
					localeText={{
						noRowsLabel: statusFilter === 'pending' ? 'No messages need review.' : 'No completed messages.',
					}}
				/>
			</Paper>
			<ResolveExtractionLogDialog
				show={selected !== null}
				row={selected}
				readOnly={selected?.status === 'done'}
				onClose={() => setSelected(null)}
				onSuccess={() => {
					setSelected(null);
					loadRows(page, pageSize, statusFilter);
				}}
				onEvidenceChanged={() => loadRows(page, pageSize, statusFilter)}
			/>
		</Box>
	);
}
